import { type BaseTemplateProps } from '../NotificationTemplate'
import { type TemplateDefinition } from '../TemplateRegistry'

interface ArIpmChangeRequestProps extends BaseTemplateProps {
  request?: {
    name?: string
    metadata?: {
      reviewUrl?: string
      review_url?: string
      dateImplemented?: string
      date_implemented?: string
    }
  }
  ams?: {
    currentGroupDisplayName?: string
    totalApprovalGroups?: number
    createdByName?: string
    approvedGroupDisplayName?: string
  }
}

type IpmTemplateKeys = {
  body: string
  bodyWithDateImplemented?: string
  title: string
  ctaLabel: string
}

const dateImplemented = (props: ArIpmChangeRequestProps): string =>
  props?.request?.metadata?.dateImplemented || props?.request?.metadata?.date_implemented || ''

const values = (props: ArIpmChangeRequestProps) => ({
  changeRequestName: props?.request?.name,
  currentGroupDisplayName: props?.ams?.currentGroupDisplayName,
  totalApprovalGroups: props?.ams?.totalApprovalGroups,
  createdByName: props?.ams?.createdByName,
  approvedGroupDisplayName: props?.ams?.approvedGroupDisplayName,
  dateImplemented: dateImplemented(props),
})

const reviewUrl = (props: ArIpmChangeRequestProps): string =>
  props?.request?.metadata?.reviewUrl || props?.request?.metadata?.review_url || ''

const defineIpmTemplate = (keys: IpmTemplateKeys): TemplateDefinition<ArIpmChangeRequestProps> => ({
  renderParams: (props: ArIpmChangeRequestProps) => ({
    messageKey:
      keys.bodyWithDateImplemented && dateImplemented(props)
        ? keys.bodyWithDateImplemented
        : keys.body,
    values: values(props),
  }),
  getTitleParams: (props: ArIpmChangeRequestProps) => ({
    messageKey: keys.title,
    values: values(props),
  }),
  getCTALabelParams: (props: ArIpmChangeRequestProps) =>
    reviewUrl(props)
      ? {
          messageKey: keys.ctaLabel,
        }
      : undefined,
  getLink: reviewUrl,
})

export const ArIpmChangeRequestCreatedNotifyAuthor = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestCreatedNotifyAuthor',
  title: 'notificationsDomain.templates.arIpmChangeRequestCreatedNotifyAuthor.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestCreatedNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestCreatedNotifyApprovers',
  title: 'notificationsDomain.templates.arIpmChangeRequestCreatedNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.review',
})

export const ArIpmChangeRequestAddedApprovalNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedApprovalNotifyApprovers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedApprovalNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedApprovalNotifyFollowers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedApprovalNotifyFollowers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedApprovalNotifyFollowers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedPartialApprovalNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedPartialApprovalNotifyApprovers',
  title:
    'notificationsDomain.templates.arIpmChangeRequestAddedPartialApprovalNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.review',
})

export const ArIpmChangeRequestAddedCommentNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedCommentNotifyApprovers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedCommentNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedCommentNotifyFollowers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedCommentNotifyFollowers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedCommentNotifyFollowers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedCancelNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedCancelNotifyApprovers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedCancelNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestAddedCancelNotifyFollowers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestAddedCancelNotifyFollowers',
  title: 'notificationsDomain.templates.arIpmChangeRequestAddedCancelNotifyFollowers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestPublishedNotifyApprovers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyApprovers',
  bodyWithDateImplemented:
    'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyApprovers.dateImplemented',
  title: 'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyApprovers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})

export const ArIpmChangeRequestPublishedNotifyFollowers = defineIpmTemplate({
  body: 'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyFollowers',
  bodyWithDateImplemented:
    'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyFollowers.dateImplemented',
  title: 'notificationsDomain.templates.arIpmChangeRequestPublishedNotifyFollowers.title',
  ctaLabel: 'notificationsDomain.templates.arIpmChangeRequest.ctaLabel.view',
})
